import { use, useMemo, useState, useRef, useLayoutEffect } from 'react';
import { HistoricalEventsDataContext } from '@/data/historical-events/historical-events-data-context';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar } from 'lucide-react';
import {
    type TimelineEvent,
    MIN_YEAR,
    MAX_YEAR,
    yearToPixel
} from './passage/timeline/timeline-utils';
import { TimelineEventModal } from './passage/timeline/TimelineEventModal';
import { filterTimelineEvents } from './passage/timeline/timeline-filtering';
import { useTimelineZoom } from './passage/timeline/useTimelineZoom';
import { useTimelineVirtualization } from './passage/timeline/useTimelineVirtualization';
import { useTimelineSwimLanes } from './passage/timeline/useTimelineSwimLanes';
import { TimelineSwimLanes, TimelineVerticalTicks, TimelineYearAxis } from './passage/timeline/TimelineRenderer';
import { TIMELINE_LAYOUT, getHeaderWidth } from './passage/timeline/timeline-config';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from './ui/button';

export function AncientWorldTimeline() {
    const historicalEventsContext = use(HistoricalEventsDataContext);

    if (!historicalEventsContext) {
        return <LoadingSkeleton />;
    }

    if (!historicalEventsContext.historicalEvents?.length) {
        return <EmptyState />;
    }

    return <AncientWorldTimelineInner historicalEvents={historicalEventsContext.historicalEvents} />;
}

interface AncientWorldTimelineInnerProps {
    historicalEvents: HistoricalEvent[];
}

function AncientWorldTimelineInner({ historicalEvents }: AncientWorldTimelineInnerProps) {
    // State - UI
    const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
    const [showWorldEvents, setShowWorldEvents] = useState(false);

    // Refs
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const timelineContainerRef = useRef<HTMLDivElement>(null);
    const shouldCenterOnPassageRef = useRef(false); // Never auto-center for this view
    const hasInitializedScrollRef = useRef(false);

    // Custom hooks for zoom and virtualization
    const {
        pixelsPerYear,
        setPixelsPerYear,
        timelineWidth,
        onControlZoomIn,
        onControlZoomOut
    } = useTimelineZoom({
        minYear: MIN_YEAR,
        maxYear: MAX_YEAR,
        timelineContainerRef,
        shouldCenterOnPassageRef
    });

    const { scrollLeft, visibleStartYear, visibleEndYear } = useTimelineVirtualization({
        scrollContainerRef,
        minYear: MIN_YEAR,
        maxYear: MAX_YEAR,
        timelineWidth
    });

    // Swimlane management hook
    const {
        visibleSwimLanes,
        handleSwimLaneToggle,
        swimLanes,
        eventsByLane
    } = useTimelineSwimLanes({
        allTimelineEvents: useMemo(() => {
            return filterTimelineEvents({
                passageEvents: undefined,
                bookEvents: undefined,
                referenceEvents: historicalEvents,
                relevanceMode: 'time-period', // Show all events
                passage: undefined,
                isLaneVisible: () => true, // Show all during filtering
                showWorldEvents
            });
        }, [historicalEvents, showWorldEvents])
    });

    // Calculate total events for display
    const totalEvents = useMemo(() => {
        return Array.from(eventsByLane.values()).reduce((sum, events) => sum + events.length, 0);
    }, [eventsByLane]);

    // Initial positioning: center on 700 BC (peak of prophetic activity and divided monarchy)
    // This provides immediate context showing Israel/Judah kings, prophets, and surrounding empires
    useLayoutEffect(() => {
        if (!scrollContainerRef.current || hasInitializedScrollRef.current) return;

        // Set optimal zoom for Ancient World view (show ~1500 years on screen)
        const optimalZoom = TIMELINE_LAYOUT.ANCIENT_WORLD_DEFAULT_ZOOM;
        if (Math.abs(pixelsPerYear - optimalZoom) > 0.01) {
            setPixelsPerYear(optimalZoom);
            return; // Let it re-render with new zoom
        }

        // Center on 700 BC - this shows:
        // - Divided Kingdom period (Israel & Judah)
        // - Major prophets (Isaiah, Jeremiah, Ezekiel)
        // - Assyrian and Babylonian empires
        // - Lead-up to exile
        const centerYear = TIMELINE_LAYOUT.ANCIENT_WORLD_DEFAULT_CENTER_YEAR;
        const centerPixel = yearToPixel(centerYear, MIN_YEAR, MAX_YEAR, timelineWidth);

        const headerWidth = getHeaderWidth();
        const containerWidth = scrollContainerRef.current.clientWidth;
        const scrollLeft = centerPixel + headerWidth - containerWidth / 2;

        scrollContainerRef.current.scrollTo({
            left: Math.max(0, scrollLeft),
            behavior: 'instant'
        });

        hasInitializedScrollRef.current = true;
    }, [pixelsPerYear, setPixelsPerYear, timelineWidth]);

    return (
        <div className="relative h-full overflow-hidden flex flex-col bg-[#f4f1ea]">
            {/* Header with controls */}
            <div className="z-20 border-b border-[#c9b896] px-4 py-3 flex items-center justify-between bg-[#f4f1ea]">
                <div className="flex items-center gap-2">
                    <h1 className="text-lg font-semibold text-[#5c4d3c]">Ancient World Timeline</h1>
                    <span className="text-sm text-[#a89575]">
                        ({totalEvents} events)
                    </span>
                </div>

                <div className="flex items-center gap-2 sm:gap-4 ml-auto overflow-x-auto no-scrollbar py-0.5">
                    {/* World Events Toggle */}
                    <label className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showWorldEvents}
                            onChange={(e) => setShowWorldEvents(e.target.checked)}
                            className="rounded border-[#c9b896] text-[#8e7e6d] focus:ring-[#8e7e6d] h-3.5 w-3.5 sm:h-4 sm:w-4 bg-transparent"
                        />
                        <span className="inline text-[#5c4d3c] font-medium whitespace-nowrap">
                            <span className="sm:hidden">Broader World</span>
                            <span className="hidden sm:inline">Broader World Events</span>
                        </span>
                    </label>

                    <div className="h-6 w-px bg-[#c9b896]/60 mx-0.5 sm:mx-0" />

                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onControlZoomOut}
                            className="h-8 w-8 p-0 border-[#c9b896] text-[#5c4d3c] hover:bg-[#e6dfcf]"
                            title="Zoom Out"
                        >
                            <ZoomOut className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onControlZoomIn}
                            className="h-8 w-8 p-0 border-[#c9b896] text-[#5c4d3c] hover:bg-[#e6dfcf]"
                            title="Zoom In"
                        >
                            <ZoomIn className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Timeline container */}
            <div
                ref={(el) => {
                    scrollContainerRef.current = el;
                    timelineContainerRef.current = el;
                }}
                className="relative overflow-x-auto overflow-y-auto flex-1"
            >
                {/* Swim lanes and axis wrapper */}
                <div className="relative min-w-full flex flex-col min-h-full" style={{ width: `${timelineWidth}px`, transition: 'none' }}>
                    <style>{`
                        @media (min-width: 640px) {
                            .timeline-container { --lane-header-width: 232px; }
                        }
                        @media (max-width: 639px) {
                            .timeline-container { --lane-header-width: 60px; }
                        }
                    `}</style>

                    <TimelineSwimLanes
                        swimLanes={swimLanes}
                        eventsByLane={eventsByLane}
                        visibleSwimLanes={visibleSwimLanes}
                        handleSwimLaneToggle={handleSwimLaneToggle}
                        timelineWidth={timelineWidth}
                        visibleStartYear={visibleStartYear}
                        visibleEndYear={visibleEndYear}
                        onEventClick={setSelectedEvent}
                    />

                    <TimelineVerticalTicks
                        timelineWidth={timelineWidth}
                        visibleStartYear={visibleStartYear}
                        visibleEndYear={visibleEndYear}
                    />

                    <TimelineYearAxis
                        timelineWidth={timelineWidth}
                        visibleStartYear={visibleStartYear}
                        visibleEndYear={visibleEndYear}
                    />
                </div>
            </div>

            {/* Event modal */}
            <TimelineEventModal
                event={selectedEvent}
                onClose={() => setSelectedEvent(null)}
            />
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div className="space-y-4 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
        </div>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center p-8 text-center h-full">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Historical Events Available</h3>
            <p className="text-sm text-muted-foreground max-w-md">
                Historical events data has not been loaded yet.
            </p>
        </div>
    );
}
